import React, { createContext, useState, useContext, useEffect } from 'react';
import { API_CONFIG } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Function to handle unauthorized (401) responses
  const handleUnauthorized = () => {
    localStorage.removeItem('adminToken');
    setUser(null);
  };

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('adminToken');
    if (token) {
      // Verify token is still valid by checking with backend
      fetch(`${API_CONFIG.baseURL}/api/admin/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(res => {
          if (res.ok) {
            return res.json();
          } else if (res.status === 401) {
            // Token is invalid or expired
            handleUnauthorized();
            return null;
          } else {
            localStorage.removeItem('adminToken');
            return null;
          }
        })
        .then(data => {
          if (data && data.success) {
            setUser({ token, username: data.user.username });
          }
        })
        .catch(() => {
          handleUnauthorized();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('adminToken', data.token);
        setUser({ token: data.token, username: data.user.username });
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, message: error.message || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    handleUnauthorized
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

